import React, { Component } from 'react';
import classNames from 'classnames';
import { Typography } from '@capintel/component-lib/typography';

import sentryUtils from '../../../utils/3rd-party/sentry';
import ZendeskUtils from '../../../utils/3rd-party/zendesk';
import PrimaryLink from '../buttons/PrimaryLink';
import { FormattedMessage } from '../../../utils/i18n';
import Nullable from '@/types/nullable';

type Props = {
  dark?: boolean;
};

type State = {
  error: Nullable<Error>;
  eventId?: string;
};

class ErrorBoundary extends Component<Props, State> {
  readonly state: State = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: Object) {
    sentryUtils.captureError(error, info).then((eventId: string) => {
      this.setState({ eventId });
    });
  }

  reload() {
    window.location.reload();
  }

  openChat() {
    ZendeskUtils.openWidget();
  }

  render() {
    const { children, dark } = this.props;
    const { error } = this.state;

    if (error) {
      return (
        <div className={classNames('error-boundary', { dark })}>
          <div className="error-content">
            <Typography variant="headerSm" as="h4">
              <i className="icon-ds-warning" />
              <FormattedMessage id="Errors.Boundary.Title" />
            </Typography>
            <div>
              <Typography variant="bodyMdRegular">
                <FormattedMessage id="Errors.Boundary.SubTitle" />
              </Typography>
              <Typography variant="bodyMdRegular">
                <FormattedMessage id="Errors.Boundary.Description" />
              </Typography>
              <div className="actions">
                <PrimaryLink onClick={this.openChat} icon="message">
                  <FormattedMessage id="Errors.Boundary.Actions.Chat" />
                </PrimaryLink>
                <PrimaryLink onClick={this.reload} icon="reload">
                  <FormattedMessage id="Errors.Boundary.Actions.Reload" />
                </PrimaryLink>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export const withErrorBoundary = (BaseComponent: React.ComponentType<any>) => (props: any) => (
  <ErrorBoundary dark={props.dark}>
    <BaseComponent {...props} />
  </ErrorBoundary>
);

export default ErrorBoundary;
